import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import FullLayout from 'layout/FullLayout';
import { installationArtists } from './data';

function normalizeImageSrc(path = '') {
  if (!path) return path;
  if (/^(?:https?:)?\/\//i.test(path) || path.startsWith('data:') || path.startsWith('/')) {
    return path;
  }
  return `/${path}`;
}

// ── Expanding accordion panel ─────────────────────────────────────────────────
function ArtistPanel({ artist, isActive, onActivate, onClick }) {
  return (
    <div
      className="relative overflow-hidden cursor-pointer"
      style={{
        flex: isActive ? '3' : '1',
        transition: 'flex 0.7s cubic-bezier(0.4,0,0.2,1)',
        minWidth: 0,
      }}
      onMouseEnter={onActivate}
      onClick={onClick}>
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-700"
        style={{
          backgroundImage: `url(${normalizeImageSrc(artist.mainImage)})`,
          transform: isActive ? 'scale(1.03)' : 'scale(1.09)',
        }}
      />

      {/* Gradient overlay */}
      <div
        className="absolute inset-0 transition-opacity duration-500"
        style={{
          background:
            'linear-gradient(to top, rgba(30,15,5,0.93) 0%, rgba(30,15,5,0.38) 48%, rgba(0,0,0,0) 100%)',
          opacity: isActive ? 0.88 : 0.96,
        }}
      />

      {/* Accent bar — top */}
      <div
        className="absolute top-0 left-0 w-full h-[3px] transition-opacity duration-500"
        style={{ backgroundColor: artist.accentColor, opacity: isActive ? 1 : 0 }}
      />

      {/* Tag badge */}
      <div
        className="absolute top-5 left-5 transition-all duration-300"
        style={{ opacity: isActive ? 1 : 0.35 }}>
        <span
          className="text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full text-white"
          style={{ backgroundColor: artist.accentColor }}>
          {artist.tag}
        </span>
      </div>

      {/* Order watermark — inactive */}
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-300"
        style={{ opacity: isActive ? 0 : 1 }}>
        <span
          className="text-[9rem] font-black leading-none select-none transition-colors duration-300"
          style={{ color: artist.accentColor, opacity: 0.9 }}>
          {artist.order}
        </span>
      </div>

      {/* Inactive: vertical name hint */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none transition-opacity duration-300 hidden lg:flex flex-col items-center gap-2">
        <div
          className="w-px h-14 rounded-full"
          style={{ backgroundColor: artist.accentColor, opacity: isActive ? 0 : 0.55 }}
        />
        {artist.collaborators ? (
          <div className="flex flex-col items-center gap-1.5">
            {artist.collaborators.map((c, i) => (
              <React.Fragment key={i}>
                <p
                  className="text-sm font-black uppercase tracking-[0.4em] text-white"
                  style={{ writingMode: 'vertical-rl' }}>
                  {c.name.split(' ')[0]}
                </p>
                {i < artist.collaborators.length - 1 && (
                  <span
                    className="w-px h-3 rounded-full"
                    style={{ backgroundColor: artist.accentColor, opacity: 0.4 }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <p
            className="text-base font-black uppercase tracking-[0.4em] text-white"
            style={{ writingMode: 'vertical-rl' }}>
            {artist.name.split(' ')[0]}
          </p>
        )}
      </div>

      {/* ── Bottom content ── */}
      <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
        {/* Portrait + artist name row */}
        <div className="flex items-end gap-3">
          {/* Portrait — active only */}
          <div
            className="flex-shrink-0 rounded-xl overflow-hidden transition-all duration-500"
            style={{
              width: isActive ? '56px' : '0',
              height: isActive ? '56px' : '0',
              opacity: isActive ? 1 : 0,
            }}>
            <img
              src={normalizeImageSrc(artist.portrait)}
              alt={artist.name}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="min-w-0 flex-1">
            <h3
              className="font-black text-white leading-tight transition-all duration-300 truncate"
              style={{ fontSize: isActive ? '1.4rem' : '0.85rem' }}>
              {artist.name}
            </h3>
            <p
              className="text-[10px] font-bold uppercase tracking-widest mt-0.5 truncate transition-colors duration-300"
              style={{ color: isActive ? artist.accentColor : 'rgba(255,255,255,0.3)' }}>
              {artist.role}
            </p>
          </div>
        </div>

        {/* Project details — active only */}
        <div
          className="transition-all duration-500 overflow-hidden"
          style={{ maxHeight: isActive ? '260px' : '0', opacity: isActive ? 1 : 0 }}>
          <div
            className="h-px w-8 rounded-full mt-4 mb-3"
            style={{ backgroundColor: artist.accentColor }}
          />
          <p className="text-white font-black text-[1.05rem] leading-snug">{artist.project.name}</p>
          <p className="text-white text-xs mt-1.5 leading-relaxed line-clamp-2">
            {artist.project.subtitle}
          </p>

          {/* Mini thumbnails */}
          <div className="flex gap-2 mt-4">
            {artist.project.images.map((img, i) => (
              <div
                key={i}
                className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 ring-1 ring-white/15">
                <img src={normalizeImageSrc(img)} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>

          {/* CTA */}
          <div
            className="inline-flex items-center gap-2 mt-5 text-sm font-black"
            style={{ color: artist.accentColor }}>
            View Full Project
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const KinstallationPage = () => {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState(installationArtists[0].id);
  const touchStartX = useRef(0);

  const activeIdx = installationArtists.findIndex((a) => a.id === activeId);
  const prevArtist = () => {
    if (activeIdx > 0) setActiveId(installationArtists[activeIdx - 1].id);
  };
  const nextArtist = () => {
    if (activeIdx < installationArtists.length - 1)
      setActiveId(installationArtists[activeIdx + 1].id);
  };
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 50) delta > 0 ? nextArtist() : prevArtist();
  };

  // Lock page scroll while on this page
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <FullLayout>
      <div className="flex overflow-hidden" style={{ height: 'calc(100vh - 60px)' }}>
        {/* ── Left Sidebar ─────────────────────────────────────────────── */}
        <div className="hidden md:flex md:w-16 lg:w-56 flex-shrink-0 border-r border-[#e8d4bc] bg-[#fcf2e9] flex-col">
          {/* Brand header */}
          <div className="px-3 lg:px-5 pt-6 pb-5 border-b border-[#e8d4bc]">
            <div className="hidden lg:block">
              <div className="flex items-center gap-2 mb-3">
                <span className="h-px w-5 bg-primary-main" />
                <span className="text-[9px] font-black uppercase tracking-[0.4em] text-primary-main">
                  Art Experience
                </span>
              </div>
              <h1 className="text-[1rem] font-black text-kin-textdark leading-none tracking-tight">
                KiN<span className="text-primary-main">STALLATION</span>
              </h1>
              <p className="text-[9px] text-kin-text/60 mt-1.5 leading-relaxed">
                Seasonal artist commissions
              </p>
            </div>
            {/* Small screens: dot */}
            <div className="lg:hidden flex items-center justify-center">
              <div className="w-4 h-4 rounded-full bg-primary-main" />
            </div>
          </div>

          {/* Artist nav tabs */}
          <div className="flex-1 flex flex-col justify-center gap-0.5 px-2 lg:px-3">
            {installationArtists.map((artist) => {
              const isActive = activeId === artist.id;
              return (
                <button
                  key={artist.id}
                  onClick={() => setActiveId(artist.id)}
                  className="group flex items-center gap-3 py-4 px-2 rounded-xl text-left w-full hover:bg-[#f0e4d4] transition-colors duration-200">
                  <div
                    className="w-[3px] rounded-full flex-shrink-0 transition-all duration-300"
                    style={{
                      height: isActive ? '40px' : '24px',
                      backgroundColor: isActive ? artist.accentColor : 'rgba(144,133,114,0.25)',
                    }}
                  />
                  <div className="min-w-0 flex-1">
                    <p
                      className="text-[10px] font-black tabular-nums transition-colors duration-300"
                      style={{ color: isActive ? artist.accentColor : 'rgba(144,133,114,0.5)' }}>
                      {artist.order}
                    </p>
                    <p
                      className="hidden lg:block text-[0.78rem] font-black leading-tight mt-0.5 truncate transition-colors duration-300"
                      style={{ color: isActive ? '#2a1a0a' : 'rgba(118,104,80,0.45)' }}>
                      {artist.name}
                    </p>
                    {isActive && (
                      <p
                        className="hidden lg:block text-[9px] truncate mt-0.5"
                        style={{ color: artist.accentColor, opacity: 0.75 }}>
                        {artist.role}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Bottom info */}
          <div className="border-t border-[#e8d4bc] px-3 lg:px-5 py-5">
            <div className="hidden lg:block space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="h-px w-3 bg-primary-main/40" />
                <p className="text-[8px] font-bold uppercase tracking-wider text-kin-text/50">
                  5 Commissioned Artists
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="h-px w-3 bg-primary-main/40" />
                <p className="text-[8px] font-bold uppercase tracking-wider text-kin-text/50">
                  4 Immersive Worlds
                </p>
              </div>
            </div>
            {/* Mobile: dot nav */}
            <div className="flex lg:hidden flex-col items-center gap-1.5">
              {installationArtists.map((a) => (
                <button
                  key={a.id}
                  onClick={() => setActiveId(a.id)}
                  className="rounded-full transition-all duration-300"
                  style={{
                    width: '6px',
                    height: activeId === a.id ? '20px' : '6px',
                    backgroundColor: activeId === a.id ? a.accentColor : 'rgba(144,133,114,0.3)',
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* ── Accordion Panels — desktop ────────────────────────────────── */}
        <div className="hidden md:flex flex-1 overflow-hidden">
          {installationArtists.map((artist) => (
            <ArtistPanel
              key={artist.id}
              artist={artist}
              isActive={activeId === artist.id}
              onActivate={() => setActiveId(artist.id)}
              onClick={() => navigate(`/kinstallation/${artist.id}`)}
            />
          ))}
        </div>

        {/* ── Mobile view ──────────────────────────────────────────────────────── */}
        <div
          className="md:hidden flex-1 relative overflow-hidden"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}>
          {installationArtists.map((artist, idx) => {
            const isActive = activeId === artist.id;
            return (
              <div
                key={artist.id}
                className="absolute inset-0 transition-opacity duration-500"
                style={{ opacity: isActive ? 1 : 0, pointerEvents: isActive ? 'auto' : 'none' }}>
                {/* BG image */}
                <img
                  src={normalizeImageSrc(artist.mainImage)}
                  alt={artist.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                {/* Gradient */}
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      'linear-gradient(to top, rgba(30,15,5,0.96) 0%, rgba(30,15,5,0.25) 52%, rgba(0,0,0,0) 100%)',
                  }}
                />

                {/* Top accent bar */}
                <div
                  className="absolute top-0 left-0 w-full h-[3px]"
                  style={{ backgroundColor: artist.accentColor }}
                />

                {/* Top info bar: title + counter */}
                <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 pt-5 z-20">
                  <span className="text-[9px] font-black uppercase tracking-[0.35em] text-white/50">
                    KiNSTALLATION
                  </span>
                  <span
                    className="text-[10px] font-black tabular-nums"
                    style={{ color: artist.accentColor }}>
                    {String(idx + 1).padStart(2, '0')} /{' '}
                    {String(installationArtists.length).padStart(2, '0')}
                  </span>
                </div>

                {/* Tag */}
                <div className="absolute top-12 left-4 z-20">
                  <span
                    className="text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full text-white"
                    style={{ backgroundColor: artist.accentColor }}>
                    {artist.tag}
                  </span>
                </div>

                {/* Order watermark */}
                <div
                  className="absolute inset-0 flex items-center justify-center pointer-events-none"
                  style={{ opacity: 0.07 }}>
                  <span
                    className="font-black leading-none select-none"
                    style={{ fontSize: '38vw', color: artist.accentColor }}>
                    {artist.order}
                  </span>
                </div>

                {/* Bottom content */}
                <div
                  className="absolute bottom-0 left-0 right-0 px-5 z-20"
                  style={{
                    paddingBottom: 'max(1.75rem, calc(5rem + env(safe-area-inset-bottom, 0px)))',
                  }}
                  onClick={() => navigate(`/kinstallation/${artist.id}`)}>
                  {artist.project.logo && (
                    <div className="inline-block bg-white/90 rounded-lg px-2.5 py-1 mb-3">
                      <img
                        src={normalizeImageSrc(artist.project.logo)}
                        alt=""
                        className="h-6 object-contain"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-11 h-11 rounded-xl overflow-hidden flex-shrink-0">
                      <img
                        src={normalizeImageSrc(artist.portrait)}
                        alt={artist.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <h3 className="text-xl font-black text-white leading-tight">{artist.name}</h3>
                      <p
                        className="text-[10px] font-bold mt-0.5 uppercase tracking-wider"
                        style={{ color: artist.accentColor }}>
                        {artist.role}
                      </p>
                    </div>
                  </div>

                  <div
                    className="h-px w-8 mb-3 rounded-full"
                    style={{ backgroundColor: artist.accentColor }}
                  />

                  <p className="text-white font-black text-sm leading-snug">
                    {artist.project.name}
                  </p>
                  <p className="text-white/40 text-xs mt-1 line-clamp-1">
                    {artist.project.subtitle}
                  </p>

                  <div className="flex gap-2 mt-3">
                    {artist.project.images.map((img, i) => (
                      <div
                        key={i}
                        className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 ring-1 ring-white/15">
                        <img
                          src={normalizeImageSrc(img)}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>

                  <div
                    className="inline-flex items-center gap-2 mt-4 text-sm font-black"
                    style={{ color: artist.accentColor }}>
                    View Full Project
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Prev arrow */}
          {activeIdx > 0 && (
            <button
              onClick={prevArtist}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full flex items-center justify-center text-white font-black text-base transition-all active:scale-90"
              style={{
                backgroundColor: 'rgba(255,255,255,0.13)',
                backdropFilter: 'blur(6px)',
                border: '1px solid rgba(255,255,255,0.15)',
              }}>
              ←
            </button>
          )}

          {/* Next arrow */}
          {activeIdx < installationArtists.length - 1 && (
            <button
              onClick={nextArtist}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full flex items-center justify-center text-white font-black text-base transition-all active:scale-90"
              style={{
                backgroundColor: 'rgba(255,255,255,0.13)',
                backdropFilter: 'blur(6px)',
                border: '1px solid rgba(255,255,255,0.15)',
              }}>
              →
            </button>
          )}
        </div>
      </div>
    </FullLayout>
  );
};

export default KinstallationPage;
